// Admin requests page.
// Side-effect import: the PDF-import button on this page (#import-pdf-btn)
// is wired by ../pdfImport.ts, which mounts the drop-zone modal at runtime
// and pre-fills the add-request form from the extracted PDF fields. Without
// this line the button is just unstyled markup.
import '../pdfImport';

// Step 1: list requests from the backend into the designer-supplied table.
// Step 2: KPI cards driven by /api/admin/requests/counts.
// Step 3: search box + period dropdown filter the list, and the KPI cards
//         double as status filters (click to filter, click again or hit
//         "Totaal" to clear). The KPI *numbers* stay global so they keep
//         alerting the admin to outstanding work even while a filter is
//         applied.
// Step 4: clicking a row opens the detail drawer with project + requester +
//         connector + period + point + notes + documents.
// Step 5: drawer carries a horizontal "workflow" stepper (click a step to
//         transition; allowed set depends on current status, hidden for
//         partner role) plus an inline note form (open to every admin
//         role). Rejection lives in the footer as a separate ghost button
//         since it's a destructive off-ramp.
// Step 6: "Procesbeheer" - five-step process panel showing the downstream
//         side-effects of the request (confirmation mail → booking →
//         DocuSign → installer instruction → final go-mail). State is
//         inferred from the request's status today; the panel is read-
//         only until the backend gains per-step timestamps + actions.
// Step 7: "+ Aanvraag toevoegen" modal - manual request entry for admins
//         taking a phone/email request on behalf of someone. Posts to the
//         public POST /api/requests endpoint so the same workflow runs
//         (confirmation email goes out automatically). Optional internal
//         note attaches via POST /admin/requests/{id}/notes after.
import { api } from '../api';
import { initAdminShell, escapeHtml, showError, hideError, confirmModal, setButtonBusy } from './shell';
import { createPagination, type PagedResult } from './pagination';
import { attachSearch } from './search';
import { t } from '../i18n/i18n';
import { fetchConnectorCatalogue, populateConnectorSelect } from '../connectorSelect';

interface RequestSummary {
  id: string;
  projectName: string;
  organisation: string;
  status: string;
  pointKind: string | null;
  pointId: number | null;
  points: { kind: string; label: string }[];
  organizationId: string | null;
  startAt: string;
  endAt: string;
  createdAt: string;
}

interface RequestCounts {
  total: number;
  new: number;
  signing: number;
  accountCreation: number;
  rejected: number;
  readyForInstall: number;
  inProgress: number;
  completed: number;
}

interface RequestNote {
  id: number;
  author: string;
  text: string;
  internal: boolean;
  createdAt: string;
}

interface RequestDocument {
  id: number;
  name: string;
  kind: string | null;
  url: string;
  uploadedBy: string | null;
  uploadedAt: string;
}

interface PowerStationPoint {
  id: number;
  powerStationId: string | null;
  city: string | null;
  street: string | null;
}

interface SigningSession {
  id: number;
  requestId: string;
  guidelineId: number;
  signerEmail: string;
  isRequired: boolean;
  opensCount: number;
  sentAt: string;
  expiresAt: string;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
}

interface RequestApproval {
  id: number;
  kind: 'operator' | 'municipality';
  email: string;
  contactName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  sentAt: string;
  expiresAt: string;
  decidedAt: string | null;
  note: string | null;
}

interface RequestDetail {
  id: string;
  status: string;
  pointKind: string | null;
  pointId: number | null;
  areaPolygon: string | null;
  organizationId: string | null;
  projectName: string;
  organisation: string;
  maxCurrentA: number | null;
  requestedConnectorType: string | null;
  wantsConstructionChargingCard: boolean | null;
  needsChargingAdapter: boolean | null;
  stationBehindFence: boolean | null;
  startAt: string;
  endAt: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  connectorName: string;
  connectorEmail: string;
  connectorPhone: string | null;
  billingName: string | null;
  billingEmail: string | null;
  billingAddress: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  billingVatNumber: string | null;
  billingReference: string | null;
  createdAt: string;
  updatedAt: string;
  signingMode: 'inhouse' | 'skipped' | null;
  connectionClass: 'standard' | 'ac_charger' | null;
  accountStatus: 'created' | 'skipped' | null;
  accountStatusAt: string | null;
  accountStatusBy: string | null;
  installPartyId: number | null;
  installPartyName: string | null;
  installPartyMode: 'notify_only' | 'key_delivery' | null;
  installPartySentAt: string | null;
  finalGoSentAt: string | null;
  notes: RequestNote[];
  documents: RequestDocument[];
  signingSessions: SigningSession[];
  approvals: RequestApproval[];
  additionalPoints: RequestPointRef[];
  points: { kind: string; id: number; label: string }[];
}

interface RequestPointRef {
  id: number;
  pointKind: string;
  pointId: number;
  orderIndex: number;
}

interface Guideline {
  id: number;
  name: string;
  version: number;
  language: string | null;
  byteSize: number;
  organizationId: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  archivedAt: string | null;
}

interface InstallationParty {
  id: number;
  name: string;
  contactEmail: string | null;
  organizationId: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  kind: string;
  organizationId: string | null;
  pointType: string | null;
}

interface ConnectorSigningHistoryEntry {
  requestId: string;
  projectName: string;
  guidelineName: string;
  guidelineVersion: number;
  acceptedAt: string;
}

interface AuditEvent {
  kind: 'note' | 'audit' | 'signing_event';
  at: string;
  actor: string;
  text: string;
  detail: string | null;
}

const PAGE_SIZE = 25;

(async () => {
  const user = await initAdminShell('requests').catch(() => null);
  if (!user) return;

  const tbody = document.getElementById('req-tbody');
  if (!tbody) return;

  const pagination = createPagination(1);
  let query = '';
  let periodDays = 0; // 0 = all periods; otherwise filter to last N days
  let activeOn = ''; // yyyy-MM-dd; '' = no event-date filter
  let statusFilter = ''; // comma-separated statuses from a KPI card; '' = all

  async function loadList(page = pagination.page): Promise<void> {
    hideError('req-error');
    try {
      const client = await api();
      const params: Record<string, string | number> = { page, pageSize: PAGE_SIZE };
      if (query) params.q = query;
      if (periodDays > 0) {
        // Last N days, anchored to "today 00:00 UTC". Using the date-only ISO
        // string keeps the URL human-readable in the network tab.
        const from = new Date();
        from.setUTCHours(0, 0, 0, 0);
        from.setUTCDate(from.getUTCDate() - periodDays + 1);
        params.from = from.toISOString();
      }
      // Matches requests whose project window contains this day (server-side
      // overlap on [StartAt, EndAt]); the date-only string avoids timezone
      // drift on the day boundary.
      if (activeOn) params.activeOn = activeOn;
      if (statusFilter) params.status = statusFilter;
      const { data } = await client.get<PagedResult<RequestSummary>>('/api/admin/requests', { params });
      render(data.items);
      pagination.render('req-prev', 'req-next', 'req-pageinfo', 'req-pagination', data.total, data.pageSize);
    } catch (err) {
      tbody!.innerHTML = '';
      showError('req-error', err instanceof Error ? err.message : t('admin.requests.err_load'));
    }
  }

  async function loadCounts(): Promise<void> {
    try {
      const client = await api();
      const { data } = await client.get<RequestCounts>('/api/admin/requests/counts');
      renderKpis(data);
    } catch {
      // Counts failure shouldn't blow up the list. KPIs stay at "-".
    }
  }

  function renderKpis(c: RequestCounts): void {
    setKpi('kpi-total',   c.total);
    setKpi('kpi-action',  c.new);                          // "Wacht op actie" - needs admin to pick up
    setKpi('kpi-review',  c.signing + c.accountCreation);  // "In behandeling" - admin is processing (signing + account step)
    setKpi('kpi-running', c.inProgress);                   // "Lopend" - currently active in the field
  }

  function setKpi(id: string, value: number): void {
    const el = document.getElementById(id);
    if (el) el.textContent = String(value);
  }

  function render(rows: RequestSummary[]): void {
    if (!tbody) return;
    if (rows.length === 0) {
      tbody.innerHTML = `<tr><td colspan="6" class="req-empty">${escapeHtml(t('admin.requests.empty'))}</td></tr>`;
      return;
    }
    tbody.innerHTML = rows.map(renderRow).join('');
  }

  function renderRow(r: RequestSummary): string {
    // Every requested device, listed flat (no "primary" point).
    const point = r.points && r.points.length > 0
      ? r.points.map((p) => `<span style="display:inline-block;background:var(--sk-blue-100,#eff3fb);border:1px solid var(--sk-border,#d6dce6);border-radius:12px;padding:1px 8px;margin:1px 3px 1px 0;font-size:12px;color:var(--sk-navy-800,#0a2a5c);white-space:nowrap;">${escapeHtml(p.label)}</span>`).join('')
      : '<span style="color:var(--sk-muted);">-</span>';
    const period = `${formatDayMonth(r.startAt)} – ${formatDayMonth(r.endAt)}`;
    const submitted = formatDate(r.createdAt);
    const statusLabel = t(`admin.request_status.${r.status}`);
    const statusClass = `status-pill--${r.status}`;
    return `
      <tr data-id="${escapeHtml(r.id)}">
        <td><span class="req-table__id">${escapeHtml(r.id)}</span></td>
        <td>
          <div class="req-table__project">${escapeHtml(r.projectName)}</div>
          <div class="req-table__org">${escapeHtml(r.organisation)}</div>
        </td>
        <td>${point}</td>
        <td class="req-table__date">${escapeHtml(period)}</td>
        <td><span class="status-pill ${statusClass}">${escapeHtml(statusLabel)}</span></td>
        <td class="req-table__date">${escapeHtml(submitted)}</td>
      </tr>
    `;
  }

  // ---- Pagination + filters ----

  pagination.attach('req-prev', 'req-next', (page) => void loadList(page));

  const searchInput = document.getElementById('search') as HTMLInputElement | null;
  if (searchInput) {
    attachSearch(searchInput, (q) => {
      query = q;
      pagination.reset();
      void loadList(1);
    });
  }

  const periodSelect = document.getElementById('filter-period') as HTMLSelectElement | null;
  if (periodSelect) {
    periodSelect.addEventListener('change', () => {
      periodDays = Number(periodSelect.value) || 0;
      pagination.reset();
      void loadList(1);
    });
  }

  const activeOnInput = document.getElementById('filter-active-on') as HTMLInputElement | null;
  if (activeOnInput) {
    activeOnInput.addEventListener('change', () => {
      activeOn = activeOnInput.value; // '' when cleared
      pagination.reset();
      void loadList(1);
    });
  }

  // KPI cards as status filters. Clicking a card filters the table to that
  // card's statuses; clicking the active card again (or "Totaal", whose
  // data-status-filter is empty) clears back to all. The counts themselves
  // stay global - see loadCounts.
  const kpiCards = Array.from(document.querySelectorAll<HTMLElement>('.kpi-card[data-status-filter]'));
  function applyKpiFilter(card: HTMLElement): void {
    statusFilter = card.classList.contains('is-active') ? '' : (card.dataset.statusFilter ?? '');
    kpiCards.forEach((c) => c.classList.toggle('is-active', statusFilter !== '' && c === card));
    pagination.reset();
    void loadList(1);
  }
  kpiCards.forEach((card) => {
    card.addEventListener('click', () => applyKpiFilter(card));
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        applyKpiFilter(card);
      }
    });
  });

  // ---- Detail drawer ----

  const drawer        = document.getElementById('drawer');
  const drawerOverlay = document.getElementById('drawer-overlay');

  function openDrawer(): void {
    drawer?.classList.add('is-open');
    drawer?.setAttribute('aria-hidden', 'false');
    drawerOverlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer(): void {
    drawer?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    drawerOverlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    // Mark the previously-open row as no longer active.
    tbody!.querySelectorAll('tr.is-active').forEach((tr) => tr.classList.remove('is-active'));
  }

  drawerOverlay?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('is-open')) closeDrawer();
  });

  let activeDetail: RequestDetail | null = null;
  let activeTemplates: EmailTemplate[] = [];
  const canTransition = user.role === 'platform_admin';

  // Re-fetched every time the drawer opens so an admin who just uploaded
  // a guideline or added an installation party on another tab sees the
  // fresh list without a hard refresh. Both endpoints are small and
  // organisation-scoped server-side, so the cost is negligible.
  async function loadGuidelines(): Promise<Guideline[]> {
    try {
      const client = await api();
      // includeArchived=false is the backend default; passing it explicitly
      // makes the contract obvious from the network tab when debugging.
      const { data } = await client.get<Guideline[]>('/api/admin/guidelines', {
        params: { includeArchived: 'false' },
      });
      return data;
    } catch (err) {
      console.error('[admin-requests] guidelines load failed', err);
      return [];
    }
  }

  async function loadParties(): Promise<InstallationParty[]> {
    try {
      const client = await api();
      const { data } = await client.get<InstallationParty[]>('/api/admin/installation-parties');
      return data;
    } catch (err) {
      console.error('[admin-requests] installation parties load failed', err);
      return [];
    }
  }

  async function loadEmailTemplates(): Promise<EmailTemplate[]> {
    try {
      const client = await api();
      const { data } = await client.get<EmailTemplate[]>('/api/admin/email-templates');
      return data;
    } catch (err) {
      // Non-fatal - without templates the dropdown is just empty and the
      // backend falls back to the (Kind, Org, PointType) chain.
      console.error('[admin-requests] email templates load failed', err);
      return [];
    }
  }

  async function loadConnectorHistory(id: string): Promise<ConnectorSigningHistoryEntry[]> {
    try {
      const client = await api();
      const { data } = await client.get<ConnectorSigningHistoryEntry[]>(`/api/admin/requests/${encodeURIComponent(id)}/signing/history`);
      return data;
    } catch (err) {
      console.error('[admin-requests] connector signing-history load failed', err);
      return [];
    }
  }

  async function loadAudit(id: string): Promise<AuditEvent[]> {
    try {
      const client = await api();
      const { data } = await client.get<AuditEvent[]>(`/api/admin/requests/${encodeURIComponent(id)}/audit`);
      return data;
    } catch (err) {
      // Audit failure shouldn't blow up the drawer - log so devtools can show it.
      console.error('[admin-requests] audit load failed', err);
      return [];
    }
  }

  async function showRequest(id: string): Promise<void> {
    if (!drawer) return;
    drawer.innerHTML = `<div class="req-drawer__body" style="padding:24px;color:var(--sk-muted);">${escapeHtml(t('admin.actions.loading'))}</div>`;
    openDrawer();
    try {
      const client = await api();
      const [detailRes, guidelines, parties, templates, history, audit] = await Promise.all([
        client.get<RequestDetail>(`/api/admin/requests/${encodeURIComponent(id)}`),
        loadGuidelines(),
        loadParties(),
        loadEmailTemplates(),
        loadConnectorHistory(id),
        loadAudit(id),
      ]);
      activeDetail = detailRes.data;
      activeTemplates = templates;
      drawer.innerHTML = renderDrawerContent(detailRes.data, canTransition, guidelines, parties, templates, history, audit);
      bindDrawer(drawer);
    } catch (err) {
      activeDetail = null;
      drawer.innerHTML = `<div class="req-drawer__body" style="padding:24px;color:var(--sk-status-occupied);">
        ${escapeHtml(err instanceof Error ? err.message : t('admin.requests.err_load'))}
      </div>`;
    }
  }

  function bindDrawer(root: HTMLElement): void {
    root.querySelector<HTMLButtonElement>('.req-drawer__close')?.addEventListener('click', closeDrawer);

    // Edit request details (PUT /admin/requests/{id}/details)
    root.querySelector<HTMLButtonElement>('#req-edit-btn')?.addEventListener('click', () => {
      if (activeDetail) openEditRequestModal(activeDetail);
    });

    // Delete request (DELETE /admin/requests/{id}) - irreversible, confirm first.
    const deleteBtn = root.querySelector<HTMLButtonElement>('#req-delete-btn');
    deleteBtn?.addEventListener('click', async () => {
      const id = deleteBtn.dataset.id;
      if (!id) return;
      if (!(await confirmModal({
        title: t('admin.request_drawer.action_delete'),
        message: t('admin.request_drawer.confirm_delete'),
        confirmLabel: t('admin.request_drawer.action_delete'),
        danger: true,
      }))) return;
      setButtonBusy(deleteBtn, true);
      try {
        const client = await api();
        await client.delete(`/api/admin/requests/${encodeURIComponent(id)}`);
        closeDrawer();
        await Promise.all([loadList(), loadCounts()]);
      } catch (err) {
        showDrawerError(err instanceof Error ? err.message : t('admin.request_drawer.err_delete'));
        setButtonBusy(deleteBtn, false);
      }
    });

    // Status transitions (PATCH /admin/requests/{id})
    root.querySelectorAll<HTMLButtonElement>('button[data-transition]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const next = btn.dataset.transition;
        if (!id || !next) return;
        // Confirm only for terminal transitions - both are hard to undo.
        if (next === 'rejected' || next === 'completed') {
          if (!(await confirmModal({
            title: t(`admin.request_drawer.action_${next}`),
            message: t(`admin.request_drawer.confirm_${next}`),
            confirmLabel: t(`admin.request_drawer.action_${next}`),
            danger: next === 'rejected',
          }))) return;
        }
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.patch(`/api/admin/requests/${encodeURIComponent(id)}`, { status: next });
          // Refresh everything that's now out of date.
          await Promise.all([showRequest(id), loadList(), loadCounts()]);
        } catch (err) {
          showDrawerError(err instanceof Error ? err.message : t('admin.request_drawer.err_transition'));
          setButtonBusy(btn, false);
        }
      });
    });

    // Signing - send link (POST /admin/requests/{id}/signing/send)
    const signingSendBtn = root.querySelector<HTMLButtonElement>('#signing-send-btn');
    signingSendBtn?.addEventListener('click', async () => {
      const id = signingSendBtn.dataset.id;
      if (!id) return;
      const modeInput = root.querySelector<HTMLInputElement>('input[name="signing-mode"]:checked')
        ?? root.querySelector<HTMLInputElement>('input[name="signing-mode"]');
      const mode = (modeInput?.value ?? 'inhouse') as 'inhouse' | 'skipped';
      const guidelineSel = root.querySelector<HTMLSelectElement>('#signing-guideline');
      const signerEmail  = root.querySelector<HTMLInputElement>('#signing-signer-email')?.value.trim() || null;
      const guidelineId  = guidelineSel?.value ? Number(guidelineSel.value) : null;
      const isRequired   = root.querySelector<HTMLInputElement>('#signing-required')?.checked ?? true;
      if (mode === 'inhouse' && !guidelineId) {
        showDrawerError(t('admin.signing.err_pick_guideline'));
        return;
      }
      setButtonBusy(signingSendBtn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/requests/${encodeURIComponent(id)}/signing/send`,
          mode === 'inhouse' ? { mode, guidelineId, signerEmail, isRequired } : { mode });
        await Promise.all([showRequest(id), loadList(), loadCounts()]);
      } catch (err) {
        showDrawerError(extractApiError(err, t('admin.signing.err_send')));
        setButtonBusy(signingSendBtn, false);
      }
    });

    // Signing - resend / revoke (one row per session; class-based delegation)
    root.querySelectorAll<HTMLButtonElement>('.signing-resend-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const sessionId = btn.dataset.sessionId;
        if (!id || !sessionId) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.post(`/api/admin/requests/${encodeURIComponent(id)}/signing/resend`, null, { params: { sessionId } });
          await showRequest(id);
        } catch (err) {
          showDrawerError(extractApiError(err, t('admin.signing.err_resend')));
          setButtonBusy(btn, false);
        }
      });
    });
    root.querySelectorAll<HTMLButtonElement>('.signing-revoke-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const sessionId = btn.dataset.sessionId;
        if (!id || !sessionId) return;
        if (!(await confirmModal({
          title: t('admin.signing.revoke'),
          message: t('admin.signing.confirm_revoke'),
          confirmLabel: t('admin.signing.revoke'),
          danger: true,
        }))) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.post(`/api/admin/requests/${encodeURIComponent(id)}/signing/revoke`, null, { params: { sessionId } });
          await showRequest(id);
        } catch (err) {
          showDrawerError(extractApiError(err, t('admin.signing.err_revoke')));
          setButtonBusy(btn, false);
        }
      });
    });
    // Signing - acceptance certificate (guideline PDF + evidence page).
    // Fetched via axios so the bearer token rides; served as a download
    // rather than a tab because the merged PDF is meant to be forwarded.
    root.querySelectorAll<HTMLButtonElement>('.signing-cert-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const sessionId = btn.dataset.sessionId;
        if (!id || !sessionId) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          const { data } = await client.get<Blob>(
            `/api/admin/requests/${encodeURIComponent(id)}/signing/${sessionId}/certificate`,
            { responseType: 'blob' });
          const url = URL.createObjectURL(data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `acceptance-certificate-${id}-s${sessionId}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
          showDrawerError(extractApiError(err, t('admin.signing.err_certificate')));
        } finally {
          setButtonBusy(btn, false);
        }
      });
    });

    // Approvals - send one or both (POST /admin/requests/{id}/approvals/send)
    root.querySelectorAll<HTMLButtonElement>('button[data-approval-send]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const kind = btn.dataset.approvalSend as 'operator' | 'municipality';
        if (!id || !kind) return;
        const email = root.querySelector<HTMLInputElement>(`input[data-approval-email="${kind}"]`)?.value.trim() ?? '';
        const contactName = root.querySelector<HTMLInputElement>(`input[data-approval-name="${kind}"]`)?.value.trim() || null;
        if (!email) { showDrawerError(t('admin.approvals.err_email')); return; }
        setButtonBusy(btn, true);
        try {
          const client = await api();
          const body: { operator?: { email: string; contactName: string | null }; municipality?: { email: string; contactName: string | null } } = {};
          body[kind] = { email, contactName };
          await client.post(`/api/admin/requests/${encodeURIComponent(id)}/approvals/send`, body);
          await showRequest(id);
        } catch (err) {
          showDrawerError(extractApiError(err, t('admin.approvals.err_send')));
          setButtonBusy(btn, false);
        }
      });
    });

    // Approvals - resend per kind
    root.querySelectorAll<HTMLButtonElement>('button[data-approval-resend]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const kind = btn.dataset.approvalResend;
        if (!id || !kind) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.post(`/api/admin/requests/${encodeURIComponent(id)}/approvals/${kind}/resend`);
          await showRequest(id);
        } catch (err) {
          showDrawerError(extractApiError(err, t('admin.approvals.err_resend')));
          setButtonBusy(btn, false);
        }
      });
    });

    // Approvals - admin force-decide (audit-logged on the backend)
    root.querySelectorAll<HTMLButtonElement>('button[data-approval-force]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id;
        const kind = btn.dataset.approvalForce;
        const status = btn.dataset.forceStatus;
        if (!id || !kind || !status) return;
        if (!(await confirmModal({
          title: status === 'approved' ? t('admin.approvals.force_approve') : t('admin.approvals.force_reject'),
          message: t('admin.approvals.force_confirm'),
          confirmLabel: status === 'approved' ? t('admin.approvals.force_approve') : t('admin.approvals.force_reject'),
          danger: status !== 'approved',
        }))) return;
        const note = window.prompt(t('admin.approvals.force_note_label')) ?? '';
        if (!note.trim()) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.post(`/api/admin/requests/${encodeURIComponent(id)}/approvals/${kind}/force`, { status, note });
          await Promise.all([showRequest(id), loadList(), loadCounts()]);
        } catch (err) {
          showDrawerError(extractApiError(err, t('admin.approvals.err_force')));
          setButtonBusy(btn, false);
        }
      });
    });

    // Install party - mode radio swap repopulates the template dropdown so
    // only templates whose Kind matches the chosen mode are pickable.
    root.querySelectorAll<HTMLInputElement>('input[name="install-mode"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const tplSel = root.querySelector<HTMLSelectElement>('#install-party-template');
        if (!tplSel) return;
        const mode = (root.querySelector<HTMLInputElement>('input[name="install-mode"]:checked')?.value ?? 'notify_only') as 'notify_only' | 'key_delivery';
        tplSel.innerHTML = renderInstallTemplateOptions(activeTemplates, mode, activeDetail?.organizationId ?? null);
      });
    });

    // Install party - send instruction (POST /admin/requests/{id}/install-party)
    root.querySelector<HTMLButtonElement>('#install-party-send-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.id;
      if (!id) return;
      const sel = root.querySelector<HTMLSelectElement>('#install-party-select');
      const partyId = Number(sel?.value ?? 0);
      if (!partyId) { showDrawerError(t('admin.install_party.err_pick')); return; }
      const mode = (root.querySelector<HTMLInputElement>('input[name="install-mode"]:checked')?.value ?? 'notify_only') as 'notify_only' | 'key_delivery';
      const emailTemplateId = root.querySelector<HTMLSelectElement>('#install-party-template')?.value || null;
      setButtonBusy(btn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/requests/${encodeURIComponent(id)}/install-party`, { installationPartyId: partyId, mode, emailTemplateId });
        await showRequest(id);
      } catch (err) {
        showDrawerError(extractApiError(err, t('admin.install_party.err_send')));
        setButtonBusy(btn, false);
      }
    });

    // Account-creation reminder - confirm + skip share the endpoint, only
    // the body field differs. Confirm requires the checkbox to be ticked
    // first so the admin can't accidentally fire it without acknowledging.
    async function postAccountStatus(id: string, status: 'created' | 'skipped', btn: HTMLButtonElement): Promise<void> {
      setButtonBusy(btn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/requests/${encodeURIComponent(id)}/account`, { status });
        await Promise.all([showRequest(id), loadList(), loadCounts()]);
      } catch (err) {
        showDrawerError(extractApiError(err, t('admin.account.err_save')));
        setButtonBusy(btn, false);
      }
    }
    root.querySelector<HTMLButtonElement>('#account-confirm-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.id;
      if (!id) return;
      const checked = root.querySelector<HTMLInputElement>('#account-created-check')?.checked ?? false;
      if (!checked) {
        showDrawerError(t('admin.account.err_unchecked'));
        return;
      }
      await postAccountStatus(id, 'created', btn);
    });
    root.querySelector<HTMLButtonElement>('#account-skip-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.id;
      if (!id) return;
      if (!(await confirmModal({
        title: t('admin.account.skip'),
        message: t('admin.account.confirm_skip'),
        confirmLabel: t('admin.account.skip'),
      }))) return;
      await postAccountStatus(id, 'skipped', btn);
    });

    // Install party - resend
    root.querySelector<HTMLButtonElement>('#install-party-resend-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.id;
      if (!id) return;
      setButtonBusy(btn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/requests/${encodeURIComponent(id)}/install-party/resend`);
        await showRequest(id);
      } catch (err) {
        showDrawerError(extractApiError(err, t('admin.install_party.err_resend')));
        setButtonBusy(btn, false);
      }
    });

    // Final go - sends to requester and transitions ready_for_install → in_progress
    root.querySelector<HTMLButtonElement>('#final-go-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.id;
      if (!id) return;
      setButtonBusy(btn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/requests/${encodeURIComponent(id)}/final-go`);
        await Promise.all([showRequest(id), loadList(), loadCounts()]);
      } catch (err) {
        showDrawerError(extractApiError(err, t('admin.final_go.err_send')));
        setButtonBusy(btn, false);
      }
    });

    // Inline note form (POST /admin/requests/{id}/notes)
    const form = root.querySelector<HTMLFormElement>('#req-note-form');
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!activeDetail) return;
      const textarea = form.querySelector<HTMLTextAreaElement>('textarea[name="text"]');
      const internalEl = form.querySelector<HTMLInputElement>('input[name="internal"]');
      const text = textarea?.value.trim() ?? '';
      if (!text) return;
      const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      setButtonBusy(submitBtn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/requests/${encodeURIComponent(activeDetail.id)}/notes`, {
          text,
          internal: internalEl?.checked ?? false,
        });
        await showRequest(activeDetail.id);
      } catch (err) {
        showDrawerError(err instanceof Error ? err.message : t('admin.request_drawer.err_note'));
      } finally {
        setButtonBusy(submitBtn, false);
      }
    });
  }

  // Edit-request modal - built on demand and pre-filled from the open
  // drawer's detail. Submits the editable fields to PUT .../details, which
  // re-syncs bookings and emails the requester about the change.
  function openEditRequestModal(detail: RequestDetail): void {
    const existing = document.getElementById('edit-modal-overlay');
    existing?.remove();

    const tt = (k: string) => escapeHtml(t(k));
    const v = (s: string | null) => escapeHtml(s ?? '');
    const overlay = document.createElement('div');
    overlay.id = 'edit-modal-overlay';
    overlay.className = 'modal-overlay is-open';
    overlay.setAttribute('aria-hidden', 'false');
    overlay.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="edit-modal-title">
        <div class="modal__head">
          <h2 id="edit-modal-title">${tt('admin.request_drawer.edit_title')} - ${escapeHtml(detail.id)}</h2>
          <button type="button" class="modal__close" id="edit-modal-close" aria-label="${tt('common.btn.close')}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal__body">
          <p id="edit-modal-error" class="login-error" hidden style="margin:0 0 12px;"></p>
          <p style="font-size:13px;color:var(--sk-muted);margin:0 0 16px;">${tt('admin.request_drawer.edit_lede')}</p>
          <form id="edit-request-form">
            <div class="form-row">
              <div class="form-field form-field--required">
                <label for="edit-project">${tt('admin.add_request.label_project')}</label>
                <input id="edit-project" name="project_name" type="text" required value="${v(detail.projectName)}"/>
              </div>
              <div class="form-field form-field--required">
                <label for="edit-org">${tt('admin.add_request.label_org')}</label>
                <input id="edit-org" name="organisation" type="text" required value="${v(detail.organisation)}"/>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field form-field--required">
                <label for="edit-start">${tt('admin.add_request.label_start')}</label>
                <input id="edit-start" name="start_at" type="date" required value="${v(detail.startAt.slice(0, 10))}"/>
              </div>
              <div class="form-field form-field--required">
                <label for="edit-end">${tt('admin.add_request.label_end')}</label>
                <input id="edit-end" name="end_at" type="date" required value="${v(detail.endAt.slice(0, 10))}"/>
              </div>
            </div>
            <div class="form-field">
              <label for="edit-current">${tt('admin.add_request.label_current_a')}</label>
              <input id="edit-current" name="max_current_a" type="number" min="1" value="${detail.maxCurrentA ?? ''}"/>
            </div>
            <div class="form-row">
              <div class="form-field form-field--required">
                <label for="edit-contact-name">${tt('admin.add_request.label_contact_name')}</label>
                <input id="edit-contact-name" name="contact_name" type="text" required value="${v(detail.contactName)}"/>
              </div>
              <div class="form-field form-field--required">
                <label for="edit-contact-phone">${tt('admin.add_request.label_phone')}</label>
                <input id="edit-contact-phone" name="contact_phone" type="tel" value="${v(detail.contactPhone)}"/>
              </div>
            </div>
            <div class="form-field form-field--required">
              <label for="edit-contact-email">${tt('admin.add_request.label_email')}</label>
              <input id="edit-contact-email" name="contact_email" type="email" required value="${v(detail.contactEmail)}"/>
            </div>
            <div class="form-row">
              <div class="form-field form-field--required">
                <label for="edit-connector-name">${tt('admin.add_request.label_connector_name')}</label>
                <input id="edit-connector-name" name="connector_name" type="text" required value="${v(detail.connectorName)}"/>
              </div>
              <div class="form-field form-field--required">
                <label for="edit-connector-email">${tt('admin.add_request.label_connector_email')}</label>
                <input id="edit-connector-email" name="connector_email" type="email" required value="${v(detail.connectorEmail)}"/>
              </div>
            </div>
            <div class="form-field">
              <label for="edit-connector-phone">${tt('admin.add_request.label_phone')}</label>
              <input id="edit-connector-phone" name="connector_phone" type="tel" value="${v(detail.connectorPhone)}"/>
            </div>
          </form>
        </div>
        <div class="modal__foot">
          <button type="button" class="btn btn--ghost" id="edit-modal-cancel">${tt('admin.actions.cancel')}</button>
          <button type="button" class="btn btn--primary" id="edit-modal-submit">${tt('admin.request_drawer.edit_submit')}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    const form = overlay.querySelector<HTMLFormElement>('#edit-request-form')!;
    const errorEl = overlay.querySelector<HTMLElement>('#edit-modal-error')!;
    const submitBtn = overlay.querySelector<HTMLButtonElement>('#edit-modal-submit')!;
    const close = (): void => { overlay.remove(); document.body.style.overflow = ''; };
    overlay.querySelector('#edit-modal-close')?.addEventListener('click', close);
    overlay.querySelector('#edit-modal-cancel')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    submitBtn.addEventListener('click', async () => {
      if (!form.reportValidity()) return;
      const data = new FormData(form);
      const val = (k: string) => String(data.get(k) ?? '').trim();
      const valOrNull = (k: string) => val(k) || null;
      const startAt = val('start_at');
      const endAt = val('end_at');
      if (new Date(endAt) < new Date(startAt)) {
        errorEl.textContent = t('admin.add_request.err_dates');
        errorEl.hidden = false;
        return;
      }
      const currentRaw = val('max_current_a');
      setButtonBusy(submitBtn, true);
      try {
        const client = await api();
        await client.put(`/api/admin/requests/${encodeURIComponent(detail.id)}/details`, {
          projectName: val('project_name'),
          organisation: val('organisation'),
          maxCurrentA: currentRaw ? Number(currentRaw) : null,
          startAt: new Date(startAt).toISOString(),
          endAt: new Date(endAt).toISOString(),
          contactName: val('contact_name'),
          contactEmail: val('contact_email'),
          contactPhone: valOrNull('contact_phone'),
          connectorName: val('connector_name'),
          connectorEmail: val('connector_email'),
          connectorPhone: valOrNull('connector_phone'),
        });
        close();
        await Promise.all([showRequest(detail.id), loadList(), loadCounts()]);
      } catch (err) {
        errorEl.textContent = extractApiError(err, t('admin.request_drawer.edit_err'));
        errorEl.hidden = false;
        setButtonBusy(submitBtn, false);
      }
    });
  }

  // Row click → open drawer. Delegated so it survives re-renders.
  tbody.addEventListener('click', (e) => {
    const tr = (e.target as HTMLElement | null)?.closest<HTMLTableRowElement>('tr[data-id]');
    if (!tr) return;
    tbody!.querySelectorAll('tr.is-active').forEach((row) => row.classList.remove('is-active'));
    tr.classList.add('is-active');
    void showRequest(tr.dataset.id!);
  });

  // ---- Add request modal (manual entry by admin) ----

  const addOverlay  = document.getElementById('add-modal-overlay') as HTMLElement | null;
  const addForm     = document.getElementById('add-request-form') as HTMLFormElement | null;
  const addError    = document.getElementById('add-modal-error') as HTMLElement | null;
  const addSubmit   = document.getElementById('add-modal-submit') as HTMLButtonElement | null;
  const pointSelect = document.getElementById('add-point') as HTMLSelectElement | null;
  const pointsList  = document.getElementById('add-points-list') as HTMLDivElement | null;
  // Connector-type dropdown for the manual-add form - full catalogue + "Unknown".
  void (async () => {
    const sel = document.getElementById('add-connector') as HTMLSelectElement | null;
    if (sel) populateConnectorSelect(sel, [], await fetchConnectorCatalogue(), {
      placeholder: t('admin.add_request.connector_ph'),
      unknown: t('admin.add_request.connector_unknown'),
    });
  })();
  let pointsLoaded = false;
  /** Picked stations for the manual-add form. First is the primary. */
  const addPickedPoints: PowerStationPoint[] = [];

  function renderAddPickedPoints(): void {
    if (!pointsList) return;
    pointsList.innerHTML = addPickedPoints.map((p, idx) => {
      const label = [p.powerStationId, [p.city, p.street].filter(Boolean).join(' · ')]
        .filter(Boolean).join(' - ') || `#${p.id}`;
      return `
        <span class="add-points-list__chip"
              style="display:inline-flex;align-items:center;gap:6px;background:var(--sk-blue-100,#eff3fb);
                     border:1px solid var(--sk-border,#d6dce6);border-radius:14px;padding:4px 10px 4px 12px;
                     font-size:12.5px;color:var(--sk-navy-800,#0a2a5c);">
          <span style="font-weight:600;">${escapeHtml(label)}</span>
          <button type="button" data-remove-idx="${idx}" aria-label="Verwijder"
                  style="background:transparent;border:0;cursor:pointer;color:var(--sk-muted);font-size:15px;line-height:1;padding:0 2px;">×</button>
        </span>`;
    }).join('');
    pointsList.querySelectorAll<HTMLButtonElement>('button[data-remove-idx]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = Number(btn.dataset.removeIdx);
        if (Number.isFinite(i) && i >= 0 && i < addPickedPoints.length) {
          addPickedPoints.splice(i, 1);
          renderAddPickedPoints();
        }
      });
    });
  }

  // Dropdown acts as an "add to selection" picker: pick a station, it
  // becomes a chip, the select resets to the placeholder ready for the
  // next pick. Duplicates are silently ignored.
  pointSelect?.addEventListener('change', () => {
    const id = Number(pointSelect.value);
    if (!Number.isFinite(id) || id <= 0) return;
    if (addPickedPoints.some((p) => p.id === id)) {
      pointSelect.value = '';
      return;
    }
    const opt = pointSelect.options[pointSelect.selectedIndex];
    const station: PowerStationPoint = {
      id,
      powerStationId: opt.dataset.psId ?? null,
      city:           opt.dataset.city  ?? null,
      street:         opt.dataset.street ?? null,
    };
    addPickedPoints.push(station);
    renderAddPickedPoints();
    pointSelect.value = '';
  });

  async function ensurePointsLoaded(): Promise<void> {
    if (pointsLoaded || !pointSelect) return;
    try {
      const client = await api();
      const { data } = await client.get<PowerStationPoint[]>('/api/map/power-stations');
      // Sort by city then street so the dropdown's grouping reads naturally.
      data.sort((a, b) =>
        (a.city ?? '').localeCompare(b.city ?? '') ||
        (a.street ?? '').localeCompare(b.street ?? ''));
      const placeholder = pointSelect.querySelector('option[value=""]');
      pointSelect.innerHTML = '';
      if (placeholder) pointSelect.appendChild(placeholder);
      for (const p of data) {
        const label = [p.powerStationId, [p.city, p.street].filter(Boolean).join(' · ')]
          .filter(Boolean).join(' - ');
        const opt = document.createElement('option');
        opt.value = String(p.id);
        opt.textContent = label || `#${p.id}`;
        // Stash the display fields on the <option> so the change handler can
        // build a chip without reaching for the original `data` array.
        if (p.powerStationId) opt.dataset.psId = p.powerStationId;
        if (p.city)   opt.dataset.city   = p.city;
        if (p.street) opt.dataset.street = p.street;
        pointSelect.appendChild(opt);
      }
      pointsLoaded = true;
    } catch {
      // Leave the placeholder; submit will fail validation if no point picked.
    }
  }

  function openAddModal(): void {
    if (!addOverlay) return;
    addForm?.reset();
    addPickedPoints.length = 0;
    renderAddPickedPoints();
    if (addError) { addError.style.display = 'none'; addError.textContent = ''; }
    addOverlay.classList.add('is-open');
    addOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    void ensurePointsLoaded();
    (document.getElementById('add-org') as HTMLInputElement | null)?.focus();
  }

  function closeAddModal(): void {
    if (!addOverlay) return;
    addOverlay.classList.remove('is-open');
    addOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function showAddError(msg: string): void {
    if (!addError) return;
    addError.textContent = msg;
    addError.style.display = 'block';
  }

  document.getElementById('add-request-btn')?.addEventListener('click', openAddModal);
  // Plain navigation - a <button> instead of an <a> so all three header
  // actions share the same element type and styling.
  document.getElementById('installation-parties-btn')?.addEventListener('click', () => {
    window.location.href = 'admin-installation-parties.html';
  });
  document.getElementById('add-modal-close')?.addEventListener('click', closeAddModal);
  document.getElementById('add-modal-cancel')?.addEventListener('click', closeAddModal);
  addOverlay?.addEventListener('click', (e) => { if (e.target === addOverlay) closeAddModal(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && addOverlay?.classList.contains('is-open')) closeAddModal();
  });

  // "Kopieer van aanvrager" - fill aansluiter fields from the requester.
  // Delegated on the form so the button works regardless of when the modal
  // is built; dispatching input events makes the change observable to any
  // form-level validation listeners that might be watching.
  addForm?.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement | null)?.closest('#copy-from-org-btn');
    if (!btn) return;
    ev.preventDefault();
    const copy = (fromId: string, toId: string) => {
      const from = document.getElementById(fromId) as HTMLInputElement | null;
      const to = document.getElementById(toId) as HTMLInputElement | null;
      if (!from || !to) return;
      to.value = from.value;
      to.dispatchEvent(new Event('input', { bubbles: true }));
    };
    copy('add-contact-name',  'add-aansluiter-name');
    copy('add-contact-email', 'add-aansluiter-email');
    copy('add-contact-phone', 'add-aansluiter-phone');
  });

  addSubmit?.addEventListener('click', async () => {
    if (!addForm) return;
    if (!addForm.reportValidity()) return;

    if (addPickedPoints.length === 0) {
      showAddError(t('admin.add_request.err_no_points'));
      return;
    }

    const data = new FormData(addForm);
    const value = (k: string) => String(data.get(k) ?? '').trim();
    const valueOrNull = (k: string) => value(k) || null;

    const startAt = value('start_at');
    const endAt   = value('end_at');
    if (startAt && endAt && new Date(endAt) < new Date(startAt)) {
      showAddError(t('admin.add_request.err_dates'));
      return;
    }

    const connectorName  = value('aansluiter_name');
    const connectorEmail = value('aansluiter_email');
    const connectorPhone = value('aansluiter_phone');

    setButtonBusy(addSubmit, true);
    try {
      const client = await api();
      const [primary, ...extras] = addPickedPoints;
      const { data: created } = await client.post<{ id: string; status: string }>('/api/requests', {
        pointKind:    'PowerStation',
        pointId:      primary.id,
        additionalPoints: extras.map((p) => ({ pointKind: 'PowerStation', pointId: p.id })),
        projectName:  value('project_name'),
        organisation: value('organisation'),
        requestedConnectorType: value('requested_connector_type') || null,
        startAt:      new Date(startAt).toISOString(),
        endAt:        new Date(endAt).toISOString(),
        contactName:  value('contact_name'),
        contactEmail: value('contact_email'),
        contactPhone: valueOrNull('contact_phone'),
        connectorName,
        connectorEmail,
        connectorPhone,
      });

      // Optional internal note - only fire if the textarea has content.
      const note = value('note');
      if (note) {
        try {
          await client.post(`/api/admin/requests/${encodeURIComponent(created.id)}/notes`, {
            text: note, internal: true,
          });
        } catch {
          // Non-fatal: the request itself was created; surface the note error
          // inline only so the admin can retry from the drawer.
        }
      }

      closeAddModal();
      await Promise.all([loadList(), loadCounts()]);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err instanceof Error ? err.message : t('admin.add_request.err_save'));
      showAddError(msg);
    } finally {
      setButtonBusy(addSubmit, false);
    }
  });

  void user; // role / organizationId reserved for later steps

  // Initial load: counts once + first list page.
  await Promise.all([loadList(), loadCounts()]);

  // Deep link (?id=SK-2026-0428) opens that request's drawer, e.g. from a
  // row on the usage-history page.
  const deepLinkId = new URLSearchParams(window.location.search).get('id');
  if (deepLinkId) void showRequest(deepLinkId);
})();

// ---- Renderers (pure; live outside the IIFE so they can be unit-tested later) ----

/** Allowed status transitions, mirrors backend RequestWorkflowService.IsTransitionAllowed. */
const TRANSITIONS: Record<string, readonly string[]> = {
  new:                ['signing', 'rejected'],
  signing:            ['account_creation', 'rejected'],
  account_creation:   ['ready_for_install', 'signing', 'rejected'],
  ready_for_install:  ['in_progress', 'account_creation', 'signing'],
  in_progress:        ['completed'],
  rejected:           [],
  completed:          [],
};

/** The seven "happy path" steps shown left-to-right in the workflow stepper. Rejection is an off-ramp - rendered as a separate footer button. */
const HAPPY_PATH = ['new', 'signing', 'account_creation', 'ready_for_install', 'in_progress', 'completed'] as const;

// ---- Per-section renderers for the integration-request flow ----

function renderSigningSection(r: RequestDetail, guidelines: Guideline[], history: ConnectorSigningHistoryEntry[], canTransition: boolean): string {
  // Section is visible once the request is approved; before that the
  // workflow hasn't reached signing yet.
  const visibleStatuses = ['signing', 'ready_for_install', 'in_progress', 'completed'];
  if (!visibleStatuses.includes(r.status)) return '';

  // History banner - only relevant at the "decide whether to send a signing
  // link" stage. On later statuses it's noise; the admin already chose.
  const showHistory = r.status === 'signing' && history.length > 0;
  const historyHtml = showHistory ? renderConnectorHistory(r, history) : '';

  // Mode choice is request-level (not per-session) so it lives above the
  // sessions list. Only offered before the request has committed to a path:
  // status === provisional_approval AND no real sessions yet. Once a session
  // is sent (status → awaiting_signature) or Skip is applied, the choice is
  // locked in.
  const showModeChoice = canTransition && r.status === 'signing' && r.signingSessions.length === 0 && r.signingMode !== 'skipped';
  const modeChoice = showModeChoice ? `
    <div style="margin:0 0 14px;padding:10px 12px;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:6px;">
      <div style="font-size:11.5px;text-transform:uppercase;letter-spacing:0.05em;color:var(--sk-muted);margin-bottom:6px;">${escapeHtml(t('admin.signing.mode_choice_label'))}</div>
      <div style="display:flex;flex-wrap:wrap;gap:16px;font-size:13px;color:var(--sk-navy-800);">
        <label style="display:inline-flex;align-items:center;gap:6px;white-space:nowrap;"><input type="radio" name="signing-mode" value="inhouse" checked/> ${escapeHtml(t('admin.signing.mode_inhouse'))}</label>
        <label style="display:inline-flex;align-items:center;gap:6px;white-space:nowrap;"><input type="radio" name="signing-mode" value="skipped"/> ${escapeHtml(t('admin.signing.mode_skipped'))}</label>
      </div>
    </div>` : '';

  // Sessions arrive newest-first from the backend. We render every one of
  // them so admins managing multi-signer requests can see + act on each
  // session independently (resend / revoke per row).
  const rowsHtml = renderSigningSessions(r);
  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_signing'))}</h4>
      ${modeChoice}
      ${historyHtml}
      ${rowsHtml}
      ${canTransition ? renderSigningForm(r, guidelines) : ''}
    </div>`;
}

function renderConnectorHistory(r: RequestDetail, history: ConnectorSigningHistoryEntry[]): string {
  const intro = t('admin.signing.history_intro')
    .replace('{email}', r.connectorEmail)
    .replace('{count}', String(history.length));
  // "Recent" = signed in the past 12 months - admin can probably skip
  // re-sending the same guideline. Older signings get an amber badge so
  // the admin knows the policy has likely changed since.
  const cutoff = Date.now() - 365 * 24 * 60 * 60 * 1000;
  const items = history.map((h) => {
    const ts = new Date(h.acceptedAt).getTime();
    const recent = !Number.isNaN(ts) && ts >= cutoff;
    const badge = recent
      ? `<span style="display:inline-block;background:rgba(26,168,99,0.15);color:var(--sk-status-available);font-size:10.5px;font-weight:600;padding:1px 6px;border-radius:10px;margin-left:6px;vertical-align:middle;">${escapeHtml(t('admin.signing.history_recent_badge'))}</span>`
      : `<span style="display:inline-block;background:rgba(212,154,26,0.18);color:#8e6611;font-size:10.5px;font-weight:600;padding:1px 6px;border-radius:10px;margin-left:6px;vertical-align:middle;">${escapeHtml(t('admin.signing.history_old_badge'))}</span>`;
    return `
      <li style="padding:2px 0;">
        <strong>${escapeHtml(h.guidelineName)}</strong> v${h.guidelineVersion}${badge}
        <span style="color:var(--sk-muted);"> · ${escapeHtml(t('admin.signing.history_on_request')
          .replace('{requestId}', h.requestId)
          .replace('{project}', h.projectName)
          .replace('{at}', formatDate(h.acceptedAt)))}</span>
      </li>`;
  }).join('');
  return `
    <div role="note" style="margin:0 0 12px;padding:10px 12px;background:rgba(26,168,99,0.08);border-left:3px solid var(--sk-status-available);border-radius:4px;font-size:12.5px;color:var(--sk-navy-800);">
      <div style="margin-bottom:4px;">${escapeHtml(intro)}</div>
      <ul style="margin:4px 0 0;padding-left:18px;">${items}</ul>
    </div>`;
}

function renderSigningSessions(r: RequestDetail): string {
  if (r.signingMode === 'skipped') {
    return `<div style="color:var(--sk-muted);font-size:13px;">${escapeHtml(t('admin.signing.skipped_label'))}</div>`;
  }
  if (r.signingSessions.length === 0) {
    return `<div style="color:var(--sk-muted);font-size:13px;">${escapeHtml(t('admin.signing.no_session'))}</div>`;
  }
  return `<div style="display:grid;gap:8px;">${r.signingSessions.map(renderSigningSessionRow).join('')}</div>`;
}

function renderSigningSessionRow(session: SigningSession): string {
  const requiredBadge = session.isRequired
    ? `<span style="display:inline-block;background:var(--sk-navy-700);color:var(--sk-white);font-size:11px;font-weight:600;padding:1px 6px;border-radius:10px;margin-left:6px;">${escapeHtml(t('admin.signing.required_badge'))}</span>`
    : `<span style="display:inline-block;background:var(--sk-blue-100);color:var(--sk-navy-800);font-size:11px;font-weight:500;padding:1px 6px;border-radius:10px;margin-left:6px;">${escapeHtml(t('admin.signing.optional_badge'))}</span>`;
  const header = `<div style="font-size:13px;color:var(--sk-navy-800);"><strong>${escapeHtml(session.signerEmail)}</strong>${requiredBadge}</div>`;

  let body: string;
  let actions = '';
  if (session.acceptedAt) {
    body = `<div style="color:var(--sk-status-available);font-size:12.5px;margin-top:4px;">${escapeHtml(
      t('admin.signing.state_accepted').replace('{at}', formatDateTime(session.acceptedAt)))}</div>`;
    actions = `
      <div style="display:flex;gap:8px;margin-top:8px;">
        <button type="button" class="btn btn--ghost signing-cert-btn" data-session-id="${session.id}" data-id="${escapeHtml(session.requestId)}" style="padding:5px 10px;font-size:12px;">${escapeHtml(t('admin.signing.certificate'))}</button>
      </div>`;
  } else if (session.revokedAt) {
    body = `<div style="color:var(--sk-status-occupied);font-size:12.5px;margin-top:4px;">${escapeHtml(
      t('admin.signing.state_revoked').replace('{at}', formatDateTime(session.revokedAt)))}</div>`;
  } else {
    const sent = escapeHtml(t('admin.signing.state_sent')
      .replace('{at}', formatDateTime(session.sentAt))
      .replace('{email}', session.signerEmail));
    const opens = session.opensCount > 0 && session.lastOpenedAt
      ? `<div style="font-size:12.5px;margin-top:2px;">${escapeHtml(
          t('admin.signing.state_opened')
            .replace('{count}', String(session.opensCount))
            .replace('{at}', formatDateTime(session.lastOpenedAt)))}</div>`
      : '';
    const expires = `<div style="color:var(--sk-muted);font-size:12px;margin-top:2px;">${escapeHtml(
      t('admin.signing.expires_at').replace('{at}', formatDateTime(session.expiresAt)))}</div>`;
    body = `<div style="font-size:12.5px;color:var(--sk-status-soon);margin-top:4px;">${sent}${opens}${expires}</div>`;
    actions = `
      <div style="display:flex;gap:8px;margin-top:8px;">
        <button type="button" class="btn btn--ghost signing-resend-btn" data-session-id="${session.id}" data-id="${escapeHtml(session.requestId)}" style="padding:5px 10px;font-size:12px;">${escapeHtml(t('admin.signing.resend'))}</button>
        <button type="button" class="btn btn--ghost signing-revoke-btn" data-session-id="${session.id}" data-id="${escapeHtml(session.requestId)}" style="padding:5px 10px;font-size:12px;color:var(--sk-status-occupied);border-color:rgba(182,67,58,0.3);">${escapeHtml(t('admin.signing.revoke'))}</button>
      </div>`;
  }
  return `<div style="border:1px solid var(--sk-border);border-radius:6px;padding:10px 12px;background:var(--sk-white);">${header}${body}${actions}</div>`;
}

function renderSigningForm(r: RequestDetail, guidelines: Guideline[]): string {
  // Previously skipped → no form. Allow Send on approved AND
  // awaiting_signature so admins can append further sessions.
  if (r.signingMode === 'skipped') return '';
  if (r.status !== 'signing') return '';

  const guidelineOpts = guidelines.length === 0
    ? `<option value="">${escapeHtml(t('admin.signing.no_guidelines'))}</option>`
    : `<option value="">${escapeHtml(t('admin.signing.guideline_placeholder'))}</option>` +
      guidelines
        .map(g => `<option value="${g.id}">${escapeHtml(g.name)} v${g.version}${g.language ? ' · ' + escapeHtml(g.language) : ''}</option>`)
        .join('');

  return `
    <div id="signing-form" style="margin-top:14px;padding:14px;background:var(--sk-blue-100);border-radius:6px;">
      <div class="form-field" style="margin-bottom:14px;">
        <label for="signing-guideline">${escapeHtml(t('admin.signing.guideline_label'))}</label>
        <select id="signing-guideline">${guidelineOpts}</select>
      </div>
      <div class="form-field" style="margin-bottom:14px;">
        <label for="signing-signer-email">${escapeHtml(t('admin.signing.signer_label'))}</label>
        <input type="email" id="signing-signer-email" value="${escapeHtml(r.connectorEmail)}"/>
      </div>
      <div class="form-field" style="margin-bottom:14px;">
        <label style="display:inline-flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;white-space:nowrap;"><input type="checkbox" id="signing-required" checked/> ${escapeHtml(t('admin.signing.required_label'))}</label>
        <div style="font-size:11.5px;color:var(--sk-muted);margin-top:4px;">${escapeHtml(t('admin.signing.required_help'))}</div>
      </div>
      <button type="button" class="btn btn--primary" id="signing-send-btn" data-id="${escapeHtml(r.id)}">${escapeHtml(t('admin.signing.send'))}</button>
    </div>`;
}

function renderApprovalsSection(r: RequestDetail, canTransition: boolean): string {
  if (r.connectionClass !== 'ac_charger') return '';
  const operator = r.approvals.find(a => a.kind === 'operator');
  const municipality = r.approvals.find(a => a.kind === 'municipality');
  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_approvals'))}</h4>
      ${renderApprovalRow(r, 'operator', operator, canTransition)}
      ${renderApprovalRow(r, 'municipality', municipality, canTransition)}
    </div>`;
}

function renderAccountSection(r: RequestDetail, canTransition: boolean): string {
  // Hidden until the signing gate has passed at least once. We DO surface
  // it on ready_for_install / later statuses too so the historic decision
  // stays visible in the drawer.
  const visibleStatuses = ['account_creation', 'ready_for_install', 'in_progress', 'completed'];
  if (!visibleStatuses.includes(r.status)) return '';

  // Historic note when already decided (regardless of current status).
  let body: string;
  if (r.accountStatus) {
    const labelKey = r.accountStatus === 'created'
      ? 'admin.account.state_created'
      : 'admin.account.state_skipped';
    const tone = r.accountStatus === 'created' ? 'var(--sk-status-available)' : 'var(--sk-muted)';
    const stamp = r.accountStatusAt ? formatDateTime(r.accountStatusAt) : '-';
    const who = r.accountStatusBy ?? '-';
    body = `<div style="color:${tone};font-size:13px;">${escapeHtml(
      t(labelKey).replace('{at}', stamp).replace('{by}', who))}</div>`;
  } else if (r.status === 'account_creation' && canTransition) {
    body = `
      <div style="margin-top:4px;padding:14px;background:var(--sk-blue-100);border-radius:6px;">
        <div style="font-size:13px;color:var(--sk-navy-800);margin-bottom:12px;">${escapeHtml(t('admin.account.intro'))}</div>
        <div class="form-field" style="margin-bottom:14px;">
          <label style="display:inline-flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;white-space:nowrap;"><input type="checkbox" id="account-created-check"/> ${escapeHtml(t('admin.account.created_label'))}</label>
        </div>
        <div style="display:flex;gap:8px;">
          <button type="button" class="btn btn--primary" id="account-confirm-btn" data-id="${escapeHtml(r.id)}">${escapeHtml(t('admin.account.confirm'))}</button>
          <button type="button" class="btn btn--ghost" id="account-skip-btn" data-id="${escapeHtml(r.id)}">${escapeHtml(t('admin.account.skip'))}</button>
        </div>
      </div>`;
  } else {
    body = `<div style="color:var(--sk-muted);font-size:13px;">${escapeHtml(t('admin.account.not_yet'))}</div>`;
  }
  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_account'))}</h4>
      ${body}
    </div>`;
}

function renderApprovalRow(r: RequestDetail, kind: 'operator' | 'municipality', row: RequestApproval | undefined, canTransition: boolean): string {
  const kindLabel = t(`admin.approvals.kind_${kind}`);

  if (!row) {
    if (!canTransition) {
      return `<div style="padding:8px 0;color:var(--sk-muted);font-size:13px;">${escapeHtml(kindLabel)}: -</div>`;
    }
    return `
      <div style="padding:10px 0;border-bottom:1px solid var(--sk-border-soft,#eee);">
        <div style="font-weight:600;font-size:13px;margin-bottom:6px;">${escapeHtml(kindLabel)}</div>
        <div style="display:grid;gap:6px;">
          <input type="email" data-approval-email="${kind}" placeholder="${escapeHtml(t('admin.approvals.label_email'))}" style="padding:6px;font-size:13px;"/>
          <input type="text" data-approval-name="${kind}" placeholder="${escapeHtml(t('admin.approvals.label_contact_name'))}" style="padding:6px;font-size:13px;"/>
          <button type="button" class="btn btn--primary" data-approval-send="${kind}" data-id="${escapeHtml(r.id)}" style="padding:6px 12px;font-size:12.5px;justify-self:start;">${escapeHtml(t('admin.approvals.send'))}</button>
        </div>
      </div>`;
  }

  const stateHtml = row.status === 'pending'
    ? `<span style="color:var(--sk-status-soon);">${escapeHtml(t('admin.approvals.state_pending').replace('{at}', formatDateTime(row.expiresAt)))}</span>`
    : row.status === 'approved'
      ? `<span style="color:var(--sk-status-available);">${escapeHtml(t('admin.approvals.state_approved').replace('{at}', formatDateTime(row.decidedAt!)))}</span>`
      : `<span style="color:var(--sk-status-occupied);">${escapeHtml(t('admin.approvals.state_rejected').replace('{at}', formatDateTime(row.decidedAt!)))}</span>`;

  const note = row.note
    ? `<div style="color:var(--sk-muted);font-size:12px;margin-top:4px;">${escapeHtml(t('admin.approvals.with_note').replace('{note}', row.note))}</div>`
    : '';

  const actions = canTransition && row.status === 'pending'
    ? `<div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">
        <button type="button" class="btn btn--ghost" data-approval-resend="${kind}" data-id="${escapeHtml(r.id)}" style="padding:4px 10px;font-size:12px;">${escapeHtml(t('admin.approvals.resend'))}</button>
        <button type="button" class="btn btn--ghost" data-approval-force="${kind}" data-force-status="approved" data-id="${escapeHtml(r.id)}" style="padding:4px 10px;font-size:12px;">${escapeHtml(t('admin.approvals.force_approve'))}</button>
        <button type="button" class="btn btn--ghost" data-approval-force="${kind}" data-force-status="rejected" data-id="${escapeHtml(r.id)}" style="padding:4px 10px;font-size:12px;color:var(--sk-status-occupied);border-color:rgba(182,67,58,0.3);">${escapeHtml(t('admin.approvals.force_reject'))}</button>
      </div>`
    : '';

  return `
    <div style="padding:10px 0;border-bottom:1px solid var(--sk-border-soft,#eee);">
      <div style="font-weight:600;font-size:13px;">${escapeHtml(kindLabel)}: ${escapeHtml(row.email)}</div>
      <div style="font-size:12.5px;margin-top:2px;">${stateHtml}</div>
      ${note}
      ${actions}
    </div>`;
}

// Templates relevant to the chosen mode, with the org-scoped ones first
// and global defaults below - same priority the backend uses in the
// (Kind, Org, PointType) fallback chain, but here the admin picks one
// explicitly. An empty value means "let the backend pick" (fallback).
function renderInstallTemplateOptions(templates: EmailTemplate[], mode: 'notify_only' | 'key_delivery', requestOrgId: string | null): string {
  const expectedKind = mode === 'key_delivery' ? 'install_party_key_delivery' : 'install_party_notify_only';
  const matching = templates.filter(tpl => tpl.kind === expectedKind);
  matching.sort((a, b) => {
    const aOwn = requestOrgId && a.organizationId === requestOrgId ? 0 : 1;
    const bOwn = requestOrgId && b.organizationId === requestOrgId ? 0 : 1;
    if (aOwn !== bOwn) return aOwn - bOwn;
    if ((a.organizationId === null) !== (b.organizationId === null)) {
      return a.organizationId === null ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
  const fallback = `<option value="">${escapeHtml(t('admin.install_party.template_default'))}</option>`;
  if (matching.length === 0) return fallback;
  return fallback + matching.map(tpl => {
    const scope = tpl.organizationId === null
      ? ` · ${t('admin.install_party.template_scope_global')}`
      : '';
    return `<option value="${escapeHtml(tpl.id)}">${escapeHtml(tpl.name)}${scope}</option>`;
  }).join('');
}

function renderInstallPartySection(r: RequestDetail, parties: InstallationParty[], templates: EmailTemplate[], canTransition: boolean): string {
  // Backend refuses install-party send unless status === 'ready_for_install';
  // hide the section before that so the drawer doesn't tease unreachable controls.
  const visible = ['ready_for_install', 'in_progress', 'completed'].includes(r.status);
  if (!visible) return '';

  let body: string;
  if (r.installPartySentAt && r.installPartyName && r.installPartyMode) {
    const modeLabel = t(`admin.install_party.mode_${r.installPartyMode}`);
    body = `<div style="font-size:13px;">${escapeHtml(t('admin.install_party.state_sent')
      .replace('{name}', r.installPartyName)
      .replace('{mode}', modeLabel)
      .replace('{at}', formatDateTime(r.installPartySentAt)))}</div>`;
    if (canTransition) {
      body += `<button type="button" class="btn btn--ghost" id="install-party-resend-btn" data-id="${escapeHtml(r.id)}" style="padding:6px 12px;font-size:12.5px;margin-top:8px;">${escapeHtml(t('admin.install_party.resend'))}</button>`;
    }
  } else if (!canTransition) {
    body = `<div style="color:var(--sk-muted);font-size:12.5px;">${escapeHtml(t('admin.install_party.not_yet'))}</div>`;
  } else {
    // Install parties are organisation-scoped on the backend; only show parties
    // belonging to this request's organisation (or all for orphaned requests).
    const scoped = parties.filter(p => !r.organizationId || p.organizationId === r.organizationId);
    if (scoped.length === 0) {
      body = `<div style="color:var(--sk-muted);font-size:12.5px;">${escapeHtml(t('admin.install_party.no_parties'))}</div>`;
    } else {
      const opts = `<option value="">${escapeHtml(t('admin.install_party.installer_placeholder'))}</option>` +
        scoped.map(p => `<option value="${p.id}">${escapeHtml(p.name)}${p.contactEmail ? ' · ' + escapeHtml(p.contactEmail) : ''}</option>`).join('');
      const templateOpts = renderInstallTemplateOptions(templates, 'notify_only', r.organizationId);
      body = `
        <div style="margin-top:4px;padding:14px;background:var(--sk-blue-100);border-radius:6px;">
          <div class="form-field" style="margin-bottom:14px;">
            <label for="install-party-select">${escapeHtml(t('admin.install_party.installer_label'))}</label>
            <select id="install-party-select">${opts}</select>
          </div>
          <div class="form-field" style="margin-bottom:14px;">
            <label>${escapeHtml(t('admin.install_party.mode_label'))}</label>
            <div style="display:flex;flex-wrap:wrap;gap:16px;font-size:13px;color:var(--sk-navy-800);padding-top:4px;">
              <label style="display:inline-flex;align-items:center;gap:6px;font-weight:400;margin:0;white-space:nowrap;"><input type="radio" name="install-mode" value="notify_only" checked/> ${escapeHtml(t('admin.install_party.mode_notify_only'))}</label>
              <label style="display:inline-flex;align-items:center;gap:6px;font-weight:400;margin:0;white-space:nowrap;"><input type="radio" name="install-mode" value="key_delivery"/> ${escapeHtml(t('admin.install_party.mode_key_delivery'))}</label>
            </div>
          </div>
          <div class="form-field" style="margin-bottom:14px;">
            <label for="install-party-template">${escapeHtml(t('admin.install_party.template_label'))}</label>
            <select id="install-party-template">${templateOpts}</select>
          </div>
          <button type="button" class="btn btn--primary" id="install-party-send-btn" data-id="${escapeHtml(r.id)}">${escapeHtml(t('admin.install_party.send'))}</button>
        </div>`;
    }
  }

  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_install_party'))}</h4>
      ${body}
    </div>`;
}

function renderFinalGoSection(r: RequestDetail, canTransition: boolean): string {
  // Final-go is only meaningful after install-party briefing has gone out.
  if (!r.installPartySentAt) return '';
  let body: string;
  if (r.finalGoSentAt) {
    body = `<div style="font-size:13px;color:var(--sk-status-available);">${escapeHtml(
      t('admin.final_go.state_sent').replace('{at}', formatDateTime(r.finalGoSentAt)))}</div>`;
  } else if (canTransition) {
    body = `<button type="button" class="btn btn--primary" id="final-go-btn" data-id="${escapeHtml(r.id)}" style="padding:7px 14px;font-size:13px;">${escapeHtml(t('admin.final_go.send'))}</button>`;
  } else {
    return '';
  }
  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_final_go'))}</h4>
      ${body}
    </div>`;
}

function renderAuditSection(events: AuditEvent[]): string {
  const body = events.length === 0
    ? `<div style="color:var(--sk-muted);font-size:12.5px;">${escapeHtml(t('admin.audit.empty'))}</div>`
    : `<ul style="list-style:none;padding:0;margin:0;">
        ${events.map(renderAuditEvent).join('')}
      </ul>`;
  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_audit'))}</h4>
      ${body}
    </div>`;
}

function renderAuditEvent(e: AuditEvent): string {
  const kindLabel = t(`admin.audit.kind_${e.kind}`);
  const actor = e.kind === 'signing_event' ? t('admin.audit.actor_signer') : (e.actor || '-');
  const detail = e.detail
    ? `<div style="color:var(--sk-muted);font-size:11.5px;margin-top:2px;">${escapeHtml(e.detail)}</div>`
    : '';
  return `<li style="padding:8px 0;border-bottom:1px solid var(--sk-border-soft,#eee);font-size:13px;">
    <div style="display:flex;justify-content:space-between;gap:8px;align-items:baseline;">
      <span style="color:var(--sk-muted);font-size:11.5px;">${escapeHtml(kindLabel)} · ${escapeHtml(actor)}</span>
      <span style="color:var(--sk-muted);font-size:11.5px;">${escapeHtml(formatDateTime(e.at))}</span>
    </div>
    <div style="margin-top:2px;">${escapeHtml(e.text)}</div>
    ${detail}
  </li>`;
}

function renderWorkflowStepper(r: RequestDetail, canTransition: boolean): string {
  const allowed = TRANSITIONS[r.status] ?? [];
  const currentIdx = HAPPY_PATH.indexOf(r.status as typeof HAPPY_PATH[number]);

  const steps = HAPPY_PATH.map((step, i) => {
    const label = t(`admin.request_status.${step}`);
    const classes = ['workflow__step'];
    let disabled = true;
    let title = '';
    if (step === r.status) {
      classes.push('is-current');
      title = t('admin.request_drawer.workflow_current');
    } else if (currentIdx >= 0 && i < currentIdx) {
      classes.push('is-done');
      // Past steps in the happy path - not interactive.
    } else if (canTransition && allowed.includes(step)) {
      disabled = false;
      title = t('admin.request_drawer.workflow_clickable');
    }
    return `<button type="button" class="${classes.join(' ')}"
        data-transition="${escapeHtml(step)}" data-id="${escapeHtml(r.id)}"
        ${disabled ? 'disabled' : ''}
        ${title ? `title="${escapeHtml(title)}"` : ''}>
      ${escapeHtml(label)}
    </button>`;
  }).join('');

  return `
    <div class="req-drawer__section">
      <h4>${escapeHtml(t('admin.request_drawer.section_workflow'))}</h4>
      <div class="workflow">${steps}</div>
      ${canTransition ? `<div style="font-size:11.5px;color:var(--sk-muted);margin-top:6px;">${escapeHtml(t('admin.request_drawer.workflow_hint'))}</div>` : ''}
    </div>
  `;
}

/**
 * Area requests carry a drawn shape (GeoJSON polygon from the map) or an
 * address lookup (request wizard) in `areaPolygon` instead of a point. Surface
 * it so the operator can see WHERE the requester wants stations: a link to the
 * exact drawn shape (geojson.io) + the centre on Google Maps, or the address.
 */
function renderAreaSection(r: RequestDetail): string {
  if (!r.areaPolygon) return '';
  const label = escapeHtml(t('admin.request_drawer.section_area'));

  let parsed: { type?: string; coordinates?: number[][][]; address?: string; postalCode?: string; city?: string } | null = null;
  try { parsed = JSON.parse(r.areaPolygon); } catch { parsed = null; }

  const section = (body: string): string =>
    `<div class="req-drawer__section"><h4>${label}</h4>${body}</div>`;

  // Address-lookup fallback (request wizard - no drawn shape).
  if (parsed?.type === 'AddressLookup') {
    const line = [parsed.address, parsed.postalCode, parsed.city].filter(Boolean).map(String).join(', ');
    return section(`<div class="detail-grid__value">${escapeHtml(line || '-')}</div>`);
  }

  // GeoJSON polygon (map draw).
  const ring = parsed?.coordinates?.[0] ?? [];
  if (Array.isArray(ring) && ring.length >= 3) {
    // Drop the closing duplicate vertex for the centre calc.
    const pts = ring.length > 3 ? ring.slice(0, -1) : ring;
    const avgLng = pts.reduce((s, c) => s + c[0], 0) / pts.length;
    const avgLat = pts.reduce((s, c) => s + c[1], 0) / pts.length;
    const geojsonio = `https://geojson.io/#data=data:application/json,${encodeURIComponent(r.areaPolygon)}`;
    const gmaps = `https://www.google.com/maps?q=${avgLat},${avgLng}`;
    const corners = t('admin.request_drawer.area_corners').replace('{n}', String(pts.length));
    const link = 'color:var(--sk-blue-500);text-decoration:underline;font-size:13px;';
    return section(`
      <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-start;">
        <span style="color:var(--sk-muted);font-size:13px;">${escapeHtml(corners)} · ${avgLat.toFixed(5)}, ${avgLng.toFixed(5)}</span>
        <a href="${escapeHtml(geojsonio)}" target="_blank" rel="noopener" style="${link}">${escapeHtml(t('admin.request_drawer.area_view_exact'))}</a>
        <a href="${escapeHtml(gmaps)}" target="_blank" rel="noopener" style="${link}">${escapeHtml(t('admin.request_drawer.area_view_center'))}</a>
      </div>`);
  }

  // Unknown shape - show raw so nothing is lost.
  return section(`<pre style="white-space:pre-wrap;font-size:12px;color:var(--sk-muted);margin:0;">${escapeHtml(r.areaPolygon)}</pre>`);
}

function renderDrawerContent(r: RequestDetail, canTransition: boolean, guidelines: Guideline[], parties: InstallationParty[], templates: EmailTemplate[], history: ConnectorSigningHistoryEntry[], audit: AuditEvent[]): string {
  const statusLabel = t(`admin.request_status.${r.status}`);
  const period = `${formatDayMonth(r.startAt)} – ${formatDayMonth(r.endAt)}, ${new Date(r.endAt).getUTCFullYear()}`;
  // Every requested device, resolved server-side to a clear label (no
  // "primary" point). Falls back to the raw kind/id list if Points is absent.
  const picks = (r.points && r.points.length > 0)
    ? r.points.map((p) => p.label)
    : [
        ...(r.pointKind && r.pointId != null ? [`${r.pointKind} #${r.pointId}`] : []),
        ...(r.additionalPoints ?? []).map((p) => `${p.pointKind} #${p.pointId}`),
      ];
  const point = picks.length === 0
    ? '-'
    : picks.map((label) => `<span class="req-drawer__point-chip" style="display:inline-block;background:var(--sk-blue-100,#eff3fb);border:1px solid var(--sk-border,#d6dce6);border-radius:14px;padding:2px 9px;margin:0 4px 4px 0;font-family:inherit;font-size:12px;color:var(--sk-navy-800,#0a2a5c);">${escapeHtml(label)}</span>`).join('');

  const allowed = TRANSITIONS[r.status] ?? [];
  const showRejectBtn = canTransition && allowed.includes('rejected');

  return `
    <div class="req-drawer__head">
      <button type="button" class="req-drawer__close" aria-label="${escapeHtml(t('admin.actions.cancel'))}">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div class="req-drawer__id">${escapeHtml(r.id)}</div>
      <div class="req-drawer__title">${escapeHtml(r.projectName)}</div>
      <div class="req-drawer__org">${escapeHtml(r.organisation)}</div>
      <div class="req-drawer__pills" style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
        <span class="status-pill status-pill--${escapeHtml(r.status)}">${escapeHtml(statusLabel)}</span>
        ${canTransition && r.status !== 'rejected' ? `
          <button type="button" class="btn btn--ghost" id="req-edit-btn" style="padding:6px 12px;font-size:12.5px;white-space:nowrap;">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-2px;margin-right:5px;"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            ${escapeHtml(t('admin.request_drawer.edit_btn'))}
          </button>
        ` : ''}
      </div>
    </div>
    <div class="req-drawer__body">
      <p id="drawer-action-error" class="login-error" hidden style="margin:0 0 12px;"></p>
      ${renderWorkflowStepper(r, canTransition)}

      ${renderSigningSection(r, guidelines, history, canTransition)}
      ${renderApprovalsSection(r, canTransition)}
      ${renderAccountSection(r, canTransition)}
      ${renderInstallPartySection(r, parties, templates, canTransition)}
      ${renderFinalGoSection(r, canTransition)}

      <div class="req-drawer__section">
        <h4>${escapeHtml(t('admin.request_drawer.section_booking'))}</h4>
        <div class="booking-card">
          <div class="booking-card__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <div class="booking-card__text">
            <div class="booking-card__title">${escapeHtml(period)}</div>
            <div class="booking-card__meta">${point}${r.requestedConnectorType ? ` · ${escapeHtml(r.requestedConnectorType)}` : (r.maxCurrentA != null ? ` · ${r.maxCurrentA} A` : '')}</div>
          </div>
        </div>
      </div>

      ${renderAreaSection(r)}

      ${r.wantsConstructionChargingCard !== null ? `
        <div class="req-drawer__section">
          <h4>${escapeHtml(t('admin.request_drawer.section_charging'))}</h4>
          <div class="detail-grid">
            ${cell(t('admin.request_drawer.field_charging_card'), yesNo(r.wantsConstructionChargingCard))}
            ${cell(t('admin.request_drawer.field_adapter'), yesNo(r.needsChargingAdapter))}
            ${cell(t('admin.request_drawer.field_fenced'), yesNo(r.stationBehindFence))}
          </div>
        </div>
      ` : ''}

      <div class="req-drawer__section">
        <h4>${escapeHtml(t('admin.request_drawer.section_requester'))}</h4>
        <div class="detail-grid">
          ${cell(t('admin.request_drawer.field_name'),  escapeHtml(r.contactName))}
          ${cell(t('admin.request_drawer.field_email'), `<a href="mailto:${escapeHtml(r.contactEmail)}">${escapeHtml(r.contactEmail)}</a>`)}
          ${cell(t('admin.request_drawer.field_phone'), r.contactPhone ? escapeHtml(r.contactPhone) : '-')}
          ${cell(t('admin.request_drawer.field_owner'), r.organizationId ? escapeHtml(r.organizationId) : '-')}
        </div>
      </div>

      <div class="req-drawer__section">
        <h4>${escapeHtml(t('admin.request_drawer.section_connector'))}</h4>
        <div class="detail-grid">
          ${cell(t('admin.request_drawer.field_name'),  escapeHtml(r.connectorName))}
          ${cell(t('admin.request_drawer.field_email'), `<a href="mailto:${escapeHtml(r.connectorEmail)}">${escapeHtml(r.connectorEmail)}</a>`)}
          ${cell(t('admin.request_drawer.field_phone'), r.connectorPhone ? escapeHtml(r.connectorPhone) : '-')}
          ${cell(t('admin.request_drawer.field_submitted'), escapeHtml(formatDate(r.createdAt)))}
        </div>
      </div>

      ${hasBillingDetails(r) ? `
        <div class="req-drawer__section">
          <h4>${escapeHtml(t('admin.request_drawer.section_billing'))}</h4>
          <div class="detail-grid">
            ${cell(t('admin.request_drawer.field_billing_name'), r.billingName ? escapeHtml(r.billingName) : '-')}
            ${cell(t('admin.request_drawer.field_billing_email'), r.billingEmail ? `<a href="mailto:${escapeHtml(r.billingEmail)}">${escapeHtml(r.billingEmail)}</a>` : '-')}
            ${cell(t('admin.request_drawer.field_billing_address'), formatBillingAddress(r))}
            ${cell(t('admin.request_drawer.field_billing_vat'), r.billingVatNumber ? escapeHtml(r.billingVatNumber) : '-')}
            ${cell(t('admin.request_drawer.field_billing_reference'), r.billingReference ? escapeHtml(r.billingReference) : '-')}
          </div>
        </div>
      ` : ''}

      ${r.notes.length > 0 ? `
        <div class="req-drawer__section">
          <h4>${escapeHtml(t('admin.request_drawer.section_notes'))}</h4>
          <div class="notes-list">
            ${r.notes.map(renderNote).join('')}
          </div>
        </div>
      ` : ''}

      ${r.documents.length > 0 ? `
        <div class="req-drawer__section">
          <h4>${escapeHtml(t('admin.request_drawer.section_documents'))}</h4>
          <div class="doc-list">
            ${r.documents.map(renderDoc).join('')}
          </div>
        </div>
      ` : ''}

      <div class="req-drawer__section">
        <h4>${escapeHtml(t('admin.request_drawer.section_add_note'))}</h4>
        <form id="req-note-form" class="note-form">
          <textarea name="text" rows="3" required placeholder="${escapeHtml(t('admin.request_drawer.note_placeholder'))}"></textarea>
          <div class="note-form__row">
            <label class="note-form__check">
              <input type="checkbox" name="internal"/>
              <span>${escapeHtml(t('admin.request_drawer.note_internal'))}</span>
            </label>
            <button type="submit" class="btn btn--ghost" style="padding:7px 12px;font-size:12.5px;">${escapeHtml(t('admin.request_drawer.note_submit'))}</button>
          </div>
        </form>
      </div>

      ${renderAuditSection(audit)}
    </div>
    ${canTransition ? `
      <div class="req-drawer__foot" style="display:flex;gap:8px;justify-content:space-between;">
        ${showRejectBtn ? `
          <button type="button" class="btn btn--ghost btn--ghost-danger"
            data-transition="rejected" data-id="${escapeHtml(r.id)}"
            style="padding:9px 14px;font-size:13px;">
            ${escapeHtml(t('admin.request_drawer.action_rejected'))}
          </button>
        ` : '<span></span>'}
        <button type="button" class="btn btn--ghost btn--ghost-danger" id="req-delete-btn" data-id="${escapeHtml(r.id)}"
          style="padding:9px 14px;font-size:13px;">
          ${escapeHtml(t('admin.request_drawer.action_delete'))}
        </button>
      </div>
    ` : ''}
  `;
}

function yesNo(value: boolean | null): string {
  if (value === null) return '-';
  return escapeHtml(t(value ? 'common.yes' : 'common.no'));
}

function hasBillingDetails(r: RequestDetail): boolean {
  return !!(r.billingName || r.billingEmail || r.billingAddress
    || r.billingPostalCode || r.billingCity || r.billingVatNumber || r.billingReference);
}

// Street on the first line, "postcode city" on the second - em dash if empty.
function formatBillingAddress(r: RequestDetail): string {
  const line2 = [r.billingPostalCode, r.billingCity].filter(Boolean).join(' ');
  const lines = [r.billingAddress, line2].filter(Boolean).map((l) => escapeHtml(l as string));
  return lines.length > 0 ? lines.join('<br/>') : '-';
}

function cell(label: string, valueHtml: string): string {
  return `<div class="detail-grid__cell">
    <div class="detail-grid__label">${escapeHtml(label)}</div>
    <div class="detail-grid__value">${valueHtml}</div>
  </div>`;
}

function renderNote(n: RequestNote): string {
  const initials = (n.author || '?').slice(0, 2).toUpperCase();
  return `
    <div class="note${n.internal ? ' note--internal' : ''}">
      <div class="note__avatar">${escapeHtml(initials)}</div>
      <div class="note__bubble">
        <div class="note__head">
          <span class="note__author">${escapeHtml(n.author || '-')}</span>
          <span class="note__ts">${escapeHtml(formatDate(n.createdAt))}</span>
          ${n.internal ? `<span class="note__internal-tag">${escapeHtml(t('admin.request_drawer.internal'))}</span>` : ''}
        </div>
        <div class="note__text">${escapeHtml(n.text)}</div>
      </div>
    </div>
  `;
}

function renderDoc(d: RequestDocument): string {
  const meta = [d.kind, d.uploadedBy, formatDate(d.uploadedAt)].filter(Boolean).join(' · ');
  return `
    <div class="doc-item">
      <div class="doc-item__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      </div>
      <div>
        <div class="doc-item__name">${escapeHtml(d.name)}</div>
        ${meta ? `<div class="doc-item__meta">${escapeHtml(meta)}</div>` : ''}
      </div>
      <a class="doc-item__action" href="${escapeHtml(d.url)}" target="_blank" rel="noopener">${escapeHtml(t('admin.request_drawer.download'))}</a>
    </div>
  `;
}

const FMT_DAY_MONTH = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: 'short' });
const FMT_DATE = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
const FMT_DATETIME = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function formatDayMonth(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : FMT_DAY_MONTH.format(d);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : FMT_DATE.format(d);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : FMT_DATETIME.format(d);
}

/** Inline error banner inside the request drawer. Reuses .login-error styling. */
function showDrawerError(message: string): void {
  const box = document.getElementById('drawer-action-error');
  if (!box) return;
  box.textContent = message;
  box.hidden = false;
  box.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/** Surface the backend's `{ message: "..." }` body when present; fall back to the generic copy. */
function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: { message?: string } } }).response;
    if (resp?.data?.message) return resp.data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
